import Vue from "vue";
import Router from "vue-router";
import NotFound from "./pages/_notFound";
import store from "./store";
import slugify from "slugify";

const PassThrough = {
  render(c) {
    return c("router-view");
  },
};

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      redirect() {
        if (
          store.state.me &&
          (store.state.me.userRole.name === "ADMIN" || store.state.me.userRole.name === "EVENT_ADMIN")
        )
          return "/admin";
        return "/login";
      },
    },
    {
      path: "/user",
      component: require("./pages/user/_layout.user").default,
      children: [
        {
          path: "resetpassword/:token",
          component: require("./pages/user/password.reset").default,
        },
        {
          path: "*",
          name: "notFound",
          component: NotFound,
        },
      ],
    },
    {
      path: "/admin",
      component: require("./pages/admin/_layout.admin").default,
      meta: { authorize: ["ADMIN"] },

      beforeEnter(to, from, next) {
        if (
          (store.state.me && store.state.me.userRole.name === "ADMIN") ||
          store.state.me.userRole.name === "EVENT_ADMIN"
        )
          return next();
        return next(`/login?redirect=${to.fullPath}`);
      },
      children: [
        // nested routes, child components will be included in
        {
          path: "",
          redirect: "app",
        },
        {
          path: "app",
          components: {
            default: PassThrough,
            menu: require("./pages/admin/app/_menu.app").default,
          },
          children: [
            // App Admin
            {
              path: "",
              redirect: "events",
            },
            {
              path: "users",
              component: require("./pages/admin/app/users").default,
              meta: { authorize: ["ADMIN"] },
            },
            {
              path: "events",
              component: require("./pages/admin/app/events.list").default,
              meta: { authorize: ["ADMIN", "EVENT_ADMIN"] },
            },
            {
              path: "*",
              component: NotFound,
            },
          ],
        },
        {
          path: "event/:eventId",
          meta: { authorize: ["ADMIN", "EVENT_ADMIN"] },

          components: {
            default: PassThrough,
            menu: require("./pages/admin/event/_menu.event").default,
          },
          children: [
            // Event Admin
            {
              path: "",
              redirect: "activities",
            },
            {
              path: "activities",
              name: "activities",
              component: require("./pages/admin/event/activities.list").default,
            },
            {
              path: "activities/create",
              name: "activitiesCreate",
              component: require("./pages/admin/event/activities.edit").default,
            },
            {
              path: "activities/:id/edit",
              name: "activitiesEdit",
              component: require("./pages/admin/event/activities.edit").default,
            },
            {
              path: "activities/:id/diploma-customization",
              name: "activitiesDiplomaCustomization",
              component: require("./pages/admin/event/activities.edit.diplomaCustomization").default,
            },
            {
              path: "content",
              name: "settingsContent",
              component: require("./pages/admin/event/settings.content").default,
            },
            {
              path: "branding",
              name: "settingsBranding",
              component: require("./pages/admin/event/settings.branding").default,
            },
            {
              path: "advanced",
              name: "settingsAdvanced",
              component: require("./pages/admin/event/settings.advanced").default,
            },
            {
              path: "teams",
              name: "teams",
              component: require("./pages/admin/event/teams.list").default,
            },
            {
              path: "participants",
              name: "participants",
              component: require("./pages/admin/event/participants.list").default,
            },
            {
              path: "admins",
              name: "admins",
              meta: { authorize: ["ADMIN", "EVENT_ADMIN"] },
              component: require("./pages/admin/event/admins").default,
            },
            {
              path: "team/create",
              name: "teamCreate",
              component: require("./pages/admin/team/team.edit").default,
            },
            {
              path: "team/:id/edit",
              name: "teamEdit",
              component: require("./pages/admin/team/team.edit").default,
            },
            {
              path: "*",
              component: NotFound,
            },
          ],
        },
        {
          path: "team/:teamId",
          components: {
            default: PassThrough,
            menu: require("./pages/admin/team/_menu.team").default,
          },
          children: [
            // Team Admin,
            {
              path: "",
              redirect: "activities",
            },
            {
              path: "activities",
              component: require("./pages/admin/team/activities.list").default,
            },
            {
              path: "branding",
              component: require("./pages/admin/team/settings.branding").default,
            },
            {
              path: "content",
              component: require("./pages/admin/team/settings.content").default,
            },
            {
              path: "zoom",
              component: require("./pages/admin/team/settings.zoom").default,
            },
            {
              path: "advanced",
              component: require("./pages/admin/team/settings.advanced").default,
            },
          ],
        },
        {
          path: "*",
          component: NotFound,
        },
      ],
    },
    {
      path: "/e/:event_slug",
      component: require("./pages/participant/_layout.participant").default,
      children: [
        {
          path: "",
          component: require("./pages/participant/lobby").default,
        },
      ],
    },
    {
      path: "/online/:activity_slug",
      component: PassThrough,
      beforeEnter(to, from, next) {
        if (to.query.team) {
          const teamSlug = slugify(to.query.team, { remove: /[*+~.()'"!:@&]/g, lower: true }); //to.query.team.toLowerCase().replace(/\s/g, "-");
          delete to.query.team;
          return next({
            path: `/online/${to.params.activity_slug}/${teamSlug}/join`,
            replace: true,
            query: to.query,
          });
        }
        return next();
      },
      children: [
        {
          path: "",
          beforeEnter(to, from, next) {
            return next({
              path: to.path.replace(/\/?$/i, "/global/join"),
              replace: true,
              query: to.query,
            });
          },
        },
        {
          path: ":team_slug",
          props: (route) => ({
            globalChannel: route.params.team_slug.toLowerCase() === "global",
          }),
          component: require("./pages/participant/_layout.participant").default,
          children: [
            {
              path: "",
              beforeEnter(to, from, next) {
                return next({
                  path: to.path.replace(/\/?$/i, "/join"),
                  replace: true,
                  query: to.query,
                });
              },
            },
            {
              path: "lobby",
              beforeEnter(to, from, next) {
                return next({
                  path: to.path.replace(/lobby\/?$/i, "join"),
                  replace: true,
                  query: to.query,
                });
              },
            },
            {
              path: "live",
              beforeEnter(to, from, next) {
                return next({
                  path: to.path.replace(/live\/?$/i, "start"),
                  replace: true,
                  query: to.query,
                });
              },
            },
            {
              path: "start",
              beforeEnter(to, from, next) {
                if (store.state.activityJoined) {
                  if (store.state.selectedActivity.type == "EXTERNAL") {
                    const hasQuery = store.state.selectedActivity.externalLink.indexOf("?") !== -1;
                    const query = window.location.search && window.location.search.substring(1);
                    // TODO : add also firstName, lastName & userName if they have been typed
                    window.location.href = store.state.selectedActivity.externalLink + (hasQuery ? "&" : "?") + query;
                    return;
                  }
                  return next({
                    path: to.path.replace(/start\/?$/i, store.state.selectedActivity.type.toLowerCase()),
                    replace: true,
                    query: to.query,
                  });
                }
                return next({
                  path: to.path.replace(/start\/?$/i, "join"),
                  replace: true,
                  query: to.query,
                });
              },
            },
            {
              path: "join",
              component: require("./pages/participant/waiting.activity").default,
              beforeEnter(to, from, next) {
                if (store.state.activityJoined) {
                  return next({
                    path: to.path.replace(/join\/?$/i, "start"),
                    replace: true,
                    query: to.query,
                  });
                }
                return next();
              },
            },
            {
              path: "live_stream",
              component: require("./pages/participant/live.activity").default,
              beforeEnter(to, from, next) {
                if (!store.state.activityJoined) {
                  return next({
                    path: to.path.replace(/live_stream\/?$/i, "join"),
                    replace: true,
                    query: to.query,
                  });
                }
                return next();
              },
            },
            {
              path: "workshop",
              component: require("./pages/participant/workshop.activity").default,
              beforeEnter(to, from, next) {
                if (!store.state.activityJoined) {
                  return next({
                    path: to.path.replace(/workshop\/?$/i, "join"),
                    replace: true,
                    query: to.query,
                  });
                }
                return next();
              },
            },
          ],
        },
        {
          path: "*",
          component: NotFound,
        },
      ],
    },
    {
      path: "/login",
      beforeEnter(to, from, next) {
        if (!store.state.me) return next();
        if (to.params.redirect) return next(to.params.redirect);
        return next("/admin");
      },
      component: require("./pages/login").default,
    },
    {
      path: "/wall/:activityId",
      component: require("./pages/wall/wall").default,
    },
    {
      path: "/wall/:activitySlug/:teamSlug",
      component: require("./pages/wall/wall").default,
    },
    {
      path: "/*",
      component: NotFound,
    },
  ],
});

router.beforeEach((to, from, next) => {
  store.dispatch("AUTHENTICATE").then(() => {
    const authorize = getPartentAuthorize(to);
    if (authorize) {
      if (store.state.me && authorize.indexOf(store.state.me.userRole.name) >= 0) {
        next();
      } else {
        return next(`/login?redirect=${to.fullPath}`);
      }
    } else {
      next();
    }
  });
});
const getPartentAuthorize = (to) => {
  for (let i = to.matched.length - 1; i > 0; i--) {
    if (to.matched[i].meta.authorize) {
      return to.matched[i].meta.authorize;
    }
  }
  return null;
};
export default router;
